/** @format */

import React from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from "@mui/material";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";

const DEFAULT_PAGE_SIZE = 10;

const formatCell = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "number") {
    if (Number.isInteger(v)) return v.toLocaleString();
    return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
};

const isNumericColumn = (rows, key) => {
  let seen = false;
  for (const r of rows) {
    const v = r[key];
    if (v == null || v === "") continue;
    seen = true;
    if (typeof v !== "number" && !Number.isFinite(Number(v))) return false;
  }
  return seen;
};

const DataTable = ({ table }) => {
  const [sortKey, setSortKey] = React.useState(null);
  const [sortDir, setSortDir] = React.useState("asc");
  const [showAll, setShowAll] = React.useState(false);

  const columns = table?.columns || [];
  const rows = table?.rows || [];
  const totalRows = table?.total_rows;

  const numericByKey = React.useMemo(() => {
    const map = {};
    for (const c of columns) map[c.key] = isNumericColumn(rows, c.key);
    return map;
  }, [columns, rows]);

  const sorted = React.useMemo(() => {
    if (!sortKey) return rows;
    const isNum = numericByKey[sortKey];
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (isNum) {
        const an = Number(av);
        const bn = Number(bv);
        return sortDir === "asc" ? an - bn : bn - an;
      }
      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [rows, sortKey, sortDir, numericByKey]);

  if (!columns.length || !rows.length) return null;

  const visible = showAll ? sorted : sorted.slice(0, DEFAULT_PAGE_SIZE);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <Box sx={{ mt: 1.5, mb: 1 }}>
      <Box
        sx={{
          position: "relative",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          // Right-edge fade hints that the table scrolls horizontally on
          // narrow screens.
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: 18,
            borderRadius: "0 8px 8px 0",
            background:
              "linear-gradient(to left, rgba(255,255,255,0.9), rgba(255,255,255,0))",
            pointerEvents: "none",
            display: { xs: "block", md: "none" },
          },
        }}
      >
        <Box
          sx={{
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
        <Table size="small" sx={{ minWidth: 360 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8fafc" }}>
              {columns.map((col) => {
                const num = numericByKey[col.key];
                const sortedHere = sortKey === col.key;
                return (
                  <TableCell
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#475569",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      userSelect: "none",
                      textAlign: num ? "right" : "left",
                      borderBottom: "1px solid #e2e8f0",
                      "&:hover": { backgroundColor: "#f1f5f9" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {col.label || col.key}
                      {sortedHere ? (
                        sortDir === "asc" ? (
                          <ArrowUpwardRoundedIcon
                            sx={{ fontSize: 14, color: "#6366f1" }}
                          />
                        ) : (
                          <ArrowDownwardRoundedIcon
                            sx={{ fontSize: 14, color: "#6366f1" }}
                          />
                        )
                      ) : (
                        <UnfoldMoreRoundedIcon
                          sx={{ fontSize: 14, color: "#cbd5e1" }}
                        />
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row, i) => (
              <TableRow
                key={i}
                sx={{
                  "&:nth-of-type(odd)": {
                    backgroundColor: "rgba(241,245,249,0.4)",
                  },
                  "&:hover": { backgroundColor: "#f8fafc" },
                }}
              >
                {columns.map((col) => {
                  const num = numericByKey[col.key];
                  const raw = row[col.key];
                  return (
                    <TableCell
                      key={col.key}
                      title={raw == null ? "" : String(raw)}
                      sx={{
                        fontSize: 12,
                        color: "#1e293b",
                        textAlign: num ? "right" : "left",
                        fontFamily: num
                          ? "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
                          : "inherit",
                        maxWidth: 220,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      {formatCell(raw)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      </Box>
      {!showAll && sorted.length > DEFAULT_PAGE_SIZE && (
        <Typography
          sx={{
            fontSize: 11,
            color: "#6366f1",
            mt: 0.75,
            cursor: "pointer",
            display: "inline-block",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => setShowAll(true)}
        >
          Show all {sorted.length} rows
        </Typography>
      )}
      {totalRows != null && totalRows > rows.length && (
        <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
          Showing {rows.length} of {Number(totalRows).toLocaleString()} — ask
          "export csv" to get all.
        </Typography>
      )}
    </Box>
  );
};

export default DataTable;
